/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import type { ReactNode } from 'react';

import { formatUsd } from '@kaiizen/kai-presenters';

import {
  type ClassifiedEntry,
  formatRelativeTime,
  getAboutUserLabel,
  getActivityLogMetadataNumber,
  getActorLabel,
  type TimelineEventKey,
  useScanNavLink,
} from '@/Client/Lib';
import { styleguideColors } from '@/Client/Styles/kaiShikiTokens';
import { pxToRem } from '@/Client/Styles/pxToRem';
import { styleguideFontFamily } from '@kaiizen/kai-shiki/core';
import { LockIcon } from '@kaiizen/kai-shiki/react';

/**
 * Every entry names its own kind above the headline. Our log records discrete
 * project milestones rather than the per-task status churn the mockup also shows,
 * so the kinds here are the two the log actually distinguishes: an approval
 * outcome, and everything else that simply happened.
 */
const APPROVAL_KEYS = new Set<TimelineEventKey>([
  'scopeApproved',
  'scopeEditRequested',
  'changeOrderApprovedRejected',
  'closeoutApproved',
]);

/**
 * The rail mark's tone. The mockup fills it three ways — midtone for routine,
 * ink for a decision, green for something finished — and our entries are all
 * decisions or completions, so nothing here reads as routine.
 */
const DONE_KEYS = new Set<TimelineEventKey>(['closeoutApproved', 'scopeApproved']);

const getEntryKind = (key: TimelineEventKey) => APPROVAL_KEYS.has(key) ? 'Approval status' : 'Event';

const ROW_STYLE = css`
  label: FOCUSED_ACTIVITY_ROW_STYLE;
  position: relative;
  display: flex;
  align-items: flex-start;
  gap: 12px;

  /* The rail runs behind the marks and stops at the last entry. Spanning the row
     plus the list's 10px gap keeps it unbroken however tall a card grows. */
  &::before {
    content: '';
    position: absolute;
    left: 4px;
    top: 0;
    bottom: -10px;
    width: 1px;
    background: ${styleguideColors.stroke};
  }

  &:last-of-type::before {
    display: none;
  }
`;

const MARK_STYLE = css`
  label: FOCUSED_ACTIVITY_MARK_STYLE;
  position: relative;
  z-index: 1;
  flex: 0 0 9px;
  height: 9px;
  /* Pins the mark to the card's kind line rather than letting it float. */
  margin-top: 13px;
  background: ${styleguideColors.ink};
`;

const MARK_DONE_STYLE = css`
  ${MARK_STYLE};
  label: FOCUSED_ACTIVITY_MARK_DONE_STYLE;
  background: ${styleguideColors.success};
`;

const CARD_STYLE = css`
  label: FOCUSED_ACTIVITY_CARD_STYLE;
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 11px 14px;
  background: ${styleguideColors.white};
  border: 1px solid ${styleguideColors.stroke};
`;

const MAIN_STYLE = css`
  label: FOCUSED_ACTIVITY_MAIN_STYLE;
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const KIND_STYLE = css`
  label: FOCUSED_ACTIVITY_KIND_STYLE;
  font-family: ${styleguideFontFamily.mono};
  font-size: ${pxToRem(10)};
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: ${styleguideColors.textTertiary};
`;

const LABEL_STYLE = css`
  label: FOCUSED_ACTIVITY_LABEL_STYLE;
  margin: 0;
  font-family: ${styleguideFontFamily.body};
  font-size: ${pxToRem(14)};
  font-weight: 400;
  line-height: 1.35;
  color: ${styleguideColors.ink};

  b {
    font-weight: 700;
  }
`;

/**
 * Date over person, right-aligned against the card's edge — the two read as one
 * stamp rather than a sentence running along the bottom.
 */
const META_STYLE = css`
  label: FOCUSED_ACTIVITY_META_STYLE;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 3px;
  text-align: right;
  font-family: ${styleguideFontFamily.mono};
  font-size: ${pxToRem(10)};
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  white-space: nowrap;
`;

const WHEN_STYLE = css`
  label: FOCUSED_ACTIVITY_WHEN_STYLE;
  color: ${styleguideColors.textTertiary};
`;

const WHO_STYLE = css`
  label: FOCUSED_ACTIVITY_WHO_STYLE;
  color: ${styleguideColors.ink};
`;

const DETAIL_ROWS_STYLE = css`
  label: FOCUSED_ACTIVITY_DETAIL_ROWS_STYLE;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 3px;
`;

const DETAIL_ROW_STYLE = css`
  label: FOCUSED_ACTIVITY_DETAIL_ROW_STYLE;
  display: flex;
  align-items: baseline;
  gap: 8px;
  font-family: ${styleguideFontFamily.body};
  font-size: ${pxToRem(12)};
  line-height: 1.4;
`;

const DETAIL_LABEL_STYLE = css`
  label: FOCUSED_ACTIVITY_DETAIL_LABEL_STYLE;
  margin: 0;
  color: ${styleguideColors.textTertiary};
`;

const DETAIL_VALUE_STYLE = css`
  label: FOCUSED_ACTIVITY_DETAIL_VALUE_STYLE;
  margin: 0;
  color: ${styleguideColors.textSecondary};
`;

const DETAIL_VALUE_MONEY_STYLE = css`
  ${DETAIL_VALUE_STYLE};
  label: FOCUSED_ACTIVITY_DETAIL_VALUE_MONEY_STYLE;
  font-weight: 700;
  color: ${styleguideColors.ink};
`;

const AMOUNT_WAS_STYLE = css`
  label: FOCUSED_ACTIVITY_AMOUNT_WAS_STYLE;
  color: ${styleguideColors.textTertiary};
  text-decoration: line-through;
`;

const NAV_LINK_STYLE = css`
  label: FOCUSED_ACTIVITY_NAV_LINK_STYLE;
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 0;
  padding: 0;
  background: none;
  cursor: pointer;
  font-family: ${styleguideFontFamily.body};
  font-size: ${pxToRem(12)};
  font-weight: 700;
  color: ${styleguideColors.ink};
`;

/** A highlighter stripe through the label's lower half, as the mockup draws it. */
const NAV_LINK_LABEL_STYLE = css`
  label: FOCUSED_ACTIVITY_NAV_LINK_LABEL_STYLE;
  background: linear-gradient(${styleguideColors.accent}, ${styleguideColors.accent}) left bottom / 100% 42% no-repeat;
  padding: 0 2px;
`;

const isLockedStatus = (status?: string) => status === 'pendingReview' || status === 'inReview' || status === 'approved';

type DetailRowDescriptor =
  | { kind: 'plain' | 'money'; label: string; value: string | number }
  | { kind: 'amount'; label: string; totalAmount?: number; previousTotalAmount?: number };

const hasAmount = (totalAmount?: number, previousTotalAmount?: number) =>
  totalAmount != null || (previousTotalAmount != null && previousTotalAmount !== totalAmount);

const getDetailRows = ({ entry, key, reassignmentDetails }: ClassifiedEntry): DetailRowDescriptor[] => {
  const rows: DetailRowDescriptor[] = [];
  const aboutUserLabel = entry.aboutUserName ? getAboutUserLabel(key) : undefined;
  const approvedScopeBudget = getActivityLogMetadataNumber(entry, 'approvedScopeBudget');

  if (key === 'projectReassigned') {
    if (reassignmentDetails?.removedUsers.length) rows.push({ kind: 'plain', label: 'Removed', value: reassignmentDetails.removedUsers.join(', ') });
    if (reassignmentDetails?.addedUsers.length) rows.push({ kind: 'plain', label: 'Added', value: reassignmentDetails.addedUsers.join(', ') });
  } else if (key === 'projectHandedOff') {
    if (entry.aboutUserName) rows.push({ kind: 'plain', label: 'Waiting on', value: entry.aboutUserName });
    if (entry.details) rows.push({ kind: 'plain', label: 'Comment', value: entry.details });
  } else if (aboutUserLabel && entry.aboutUserName) {
    rows.push({ kind: 'plain', label: aboutUserLabel, value: entry.aboutUserName });
  }

  if (key === 'closeoutApproved') {
    if (entry.totalAmount != null) rows.push({ kind: 'amount', label: 'Approved amount', totalAmount: entry.totalAmount });
    if (entry.previousTotalAmount != null) rows.push({ kind: 'money', label: 'Approved change orders', value: formatUsd(entry.previousTotalAmount) });
    if (approvedScopeBudget != null) rows.push({ kind: 'money', label: 'Approved scope budget', value: formatUsd(approvedScopeBudget) });
  } else if (key === 'scopeApproved' || key === 'changeOrderApprovedRejected') {
    if (hasAmount(entry.totalAmount, entry.previousTotalAmount)) {
      rows.push({
        kind: 'amount',
        label: key === 'scopeApproved' || entry.eventType === 'changeOrderApproved' ? 'Approved amount' : 'Total amount',
        totalAmount: entry.totalAmount,
        previousTotalAmount: entry.previousTotalAmount,
      });
    }
  } else if (
    entry.totalAmount != null && entry.eventType !== 'walkCreated' && (
      key === 'initialWalkCompleted' || key === 'scopeEditRequested' || key === 'changeOrderSubmitted' || key === 'closeoutSubmitted'
    )
  ) {
    rows.push({
      kind: 'amount',
      label: entry.eventType === 'scopeSubmitted' || entry.eventType === 'scopeRejected' || key === 'changeOrderSubmitted' ? 'Pending amount' : 'Total amount',
      totalAmount: entry.totalAmount,
      previousTotalAmount: entry.previousTotalAmount,
    });
  }

  return rows;
};

// Each row is genuinely a label/value pair (Approved amount: $12,000, Field
// user: Jane Doe, ...) - <dl>/<dt>/<dd> names that relationship explicitly,
// rather than two <span>s in a <div> that only *look* paired visually.
// HTML5 explicitly permits wrapping each <dt>/<dd> pair in its own <div> for
// exactly this per-row layout/styling case.
const DetailRowView = (row: DetailRowDescriptor): ReactNode => {
  if (row.kind === 'amount') {
    const showPrevious = row.previousTotalAmount != null && row.previousTotalAmount !== row.totalAmount;
    return (
      <div css={DETAIL_ROW_STYLE}>
        <dt css={DETAIL_LABEL_STYLE}>{row.label}</dt>
        <dd css={DETAIL_VALUE_MONEY_STYLE}>
          {showPrevious ? <span css={AMOUNT_WAS_STYLE}>{`${formatUsd(row.previousTotalAmount)} `}</span> : null}
          {formatUsd(row.totalAmount)}
        </dd>
      </div>
    );
  }
  return (
    <div css={DETAIL_ROW_STYLE}>
      <dt css={DETAIL_LABEL_STYLE}>{row.label}</dt>
      <dd css={row.kind === 'money' ? DETAIL_VALUE_MONEY_STYLE : DETAIL_VALUE_STYLE}>{row.value}</dd>
    </div>
  );
};

export const FocusedActivityFeedEntry = ({ classified }: { classified: ClassifiedEntry }) => {
  const { entry, emphasis, suffix, key } = classified;
  const getNavLink = useScanNavLink();
  const navLink = getNavLink(key, entry.scanId);
  const locked = isLockedStatus(entry.scanLifecycleStatus);
  const detailRows = getDetailRows(classified);

  return (
    <li css={ROW_STYLE} data-testid={'focused-activity-feed-entry'}>
      <span css={DONE_KEYS.has(key) ? MARK_DONE_STYLE : MARK_STYLE} aria-hidden={'true'} />
      <div css={CARD_STYLE}>
        <div css={MAIN_STYLE}>
          <span css={KIND_STYLE}>{getEntryKind(key)}</span>
          <h4 css={LABEL_STYLE}>
            {emphasis} <b>{suffix}</b>
          </h4>
          {detailRows.length > 0
            ? (
              <dl css={DETAIL_ROWS_STYLE}>
                {detailRows.map((row, index) => <DetailRowView key={index} {...row} />)}
              </dl>
            )
            : null}
          {navLink
            ? (
              <button type={'button'} css={NAV_LINK_STYLE} onClick={navLink.onClick}>
                {locked ? <LockIcon size={12} aria-hidden={'true'} /> : null}
                <span css={NAV_LINK_LABEL_STYLE}>{navLink.label}</span>
                <span aria-hidden={'true'}>{'→'}</span>
              </button>
            )
            : null}
        </div>
        <div css={META_STYLE}>
          <span css={WHEN_STYLE}>{formatRelativeTime(entry.createdAt)}</span>
          <span css={WHO_STYLE}>{getActorLabel(entry)}</span>
        </div>
      </div>
    </li>
  );
};
